package com.mgacreative.hotel.feature.maintenance.ui

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.mgacreative.hotel.core.network.SupabaseNetworkClient
import com.mgacreative.hotel.core.tenant.TenantSession
import io.github.jan.supabase.postgrest.postgrest
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

@Serializable
data class MaintenanceTaskDto(
    val id: String = "",
    @SerialName("tenant_id") val tenantId: String? = null,
    @SerialName("room_id") val roomId: String? = null,
    val title: String = "",
    val description: String? = null,
    val status: String = "PENDING",
    @SerialName("created_at") val createdAt: String? = null
)

data class MaintenanceTask(
    val id: String,
    val roomId: String,
    val roomNumber: String,
    val category: String, // Klima, TV, Kapı, Elektrik, Su, Diğer
    val description: String,
    val priority: String, // DÜŞÜK, NORMAL, ACİL
    val status: String,   // AÇIK, DEVAM_EDIYOR, KAPALI
    val createdAt: String
)

class MaintenanceViewModel(
    private val supabaseClient: SupabaseNetworkClient,
    private val tenantSession: TenantSession,
    private val roomRepository: com.mgacreative.hotel.domain.repository.RoomRepository
) : ViewModel() {

    private val _tasks = MutableStateFlow<List<MaintenanceTask>>(emptyList())
    val tasks: StateFlow<List<MaintenanceTask>> = _tasks.asStateFlow()

    private val _rooms = MutableStateFlow<List<com.mgacreative.hotel.domain.model.Room>>(emptyList())
    val rooms: StateFlow<List<com.mgacreative.hotel.domain.model.Room>> = _rooms.asStateFlow()

    private val _history = MutableStateFlow<List<MaintenanceTask>>(emptyList())
    val history: StateFlow<List<MaintenanceTask>> = _history.asStateFlow()

    private val _isLoading = MutableStateFlow(false)
    val isLoading: StateFlow<Boolean> = _isLoading.asStateFlow()

    init {
        loadData()
    }

    fun loadData() {
        viewModelScope.launch {
            _isLoading.value = true
            try {
                _rooms.value = roomRepository.getRooms()
                val currentTenantId = tenantSession.currentTenantId
                if (!currentTenantId.isNullOrBlank()) {
                    val dtos = supabaseClient.client.postgrest["maintenance_tasks"]
                        .select {
                            filter {
                                eq("tenant_id", currentTenantId)
                            }
                        }
                        .decodeList<MaintenanceTaskDto>()
                    
                    val roomMap = _rooms.value.associateBy { it.id }
                    _tasks.value = dtos.map { dto ->
                        val room = roomMap[dto.roomId]
                        MaintenanceTask(
                            id = dto.id,
                            roomId = dto.roomId ?: "",
                            roomNumber = room?.roomNumber ?: "Oda",
                            category = dto.title,
                            description = dto.description ?: "",
                            priority = "NORMAL",
                            status = if (dto.status == "DONE" || dto.status == "RESOLVED") "KAPALI" else if (dto.status == "IN_PROGRESS") "DEVAM_EDIYOR" else "AÇIK",
                            createdAt = dto.createdAt?.take(10) ?: ""
                        )
                    }
                } else {
                    _tasks.value = emptyList()
                }
            } catch (e: Exception) {
                _tasks.value = emptyList()
            } finally {
                _isLoading.value = false
            }
        }
    }

    fun loadHistoryForRoom(roomId: String) {
        viewModelScope.launch {
            try {
                val currentTenantId = tenantSession.currentTenantId
                if (!currentTenantId.isNullOrBlank()) {
                    val dtos = supabaseClient.client.postgrest["maintenance_tasks"]
                        .select {
                            filter {
                                eq("tenant_id", currentTenantId)
                                eq("room_id", roomId)
                            }
                        }
                        .decodeList<MaintenanceTaskDto>()
                    val room = _rooms.value.find { it.id == roomId }
                    _history.value = dtos.map { dto ->
                        MaintenanceTask(
                            id = dto.id,
                            roomId = dto.roomId ?: "",
                            roomNumber = room?.roomNumber ?: "Oda",
                            category = dto.title,
                            description = dto.description ?: "",
                            priority = "NORMAL",
                            status = if (dto.status == "DONE" || dto.status == "RESOLVED") "KAPALI" else "AÇIK",
                            createdAt = dto.createdAt?.take(10) ?: ""
                        )
                    }
                } else {
                    _history.value = emptyList()
                }
            } catch (e: Exception) {
                _history.value = emptyList()
            }
        }
    }

    fun closeTask(taskId: String, roomId: String) {
        viewModelScope.launch {
            try {
                val currentTenantId = tenantSession.currentTenantId
                if (!currentTenantId.isNullOrBlank()) {
                    supabaseClient.client.postgrest["maintenance_tasks"].update({
                        set("status", "RESOLVED")
                    }) {
                        filter {
                            eq("id", taskId)
                            eq("tenant_id", currentTenantId)
                        }
                    }
                }
                _tasks.value = _tasks.value.filter { it.id != taskId }
                roomRepository.updateRoomStatus(roomId, "DIRTY")
            } catch (e: Exception) {
                e.printStackTrace()
            }
        }
    }

    fun createTask(roomId: String, roomNumber: String, category: String, desc: String, priority: String) {
        viewModelScope.launch {
            try {
                val currentTenantId = tenantSession.currentTenantId
                if (!currentTenantId.isNullOrBlank()) {
                    supabaseClient.client.postgrest["maintenance_tasks"].insert(
                        mapOf(
                            "tenant_id" to currentTenantId,
                            "room_id" to roomId,
                            "title" to category,
                            "description" to desc,
                            "status" to "PENDING"
                        )
                    )
                    loadData()
                    roomRepository.updateRoomStatus(roomId, "MAINTENANCE")
                }
            } catch (e: Exception) {
                e.printStackTrace()
            }
        }
    }
}

